import SportSelector from '@/components/SportSelector';
import { useParallelActivities } from '@/hooks/useParallelActivities';
import { Activity, Sports } from '@/types';
import { weekNumbers } from '@/utils/activities';
import { cumulativeSum } from '@/utils/array';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useRef, useState } from 'react';
import ActivityTotalOptions from '../components/ActivityTotalOptions';
import Head from '../components/Head';
import { NoData } from '../components/NoData';
import SkeletonLoader from '../components/SkeletonLoader';
import styles from '../style.module.css';
import { titleStyle } from '../utils/common';
import { formatWeekNumber, years } from '../utils/date';
import { DataType, dataTypeLabels, dataTypes } from '../utils/enum';
import { getTriathlonStats } from '../utils/getTriathlonStats';

const colorsByyear = {
  2019: '#FF006E',
  2020: '#FFBE0B',
  2021: '#FB5607',
  2022: '#FF006E',
  2023: '#8338EC',
  2024: '#3A86FF',
  2025: '#00C9A7',
};

const ActivitiesChart = () => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const [sport, setSport] = useState<Sports>('running');
  const [activityTotal, setActivityTotal] = useState<
    Exclude<DataType, 'suffer_score'>
  >(dataTypes[0]);

  const handleRadioChangeActivityTotal = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setActivityTotal(value as Exclude<DataType, 'suffer_score'>);
  };

  const handleRadioChangeSport = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSport(event.target.value as Sports);

  const parallelActivities = useParallelActivities(years);
  const parallelActivitiesObject = parallelActivities
    .map((result) => result.data)
    .reduce(
      (acc, activity, index) => ({
        ...acc,
        [years[index]]: activity || [],
      }),
      {} as Record<number, Activity[]>
    );
  const isLoadingParallel = parallelActivities.some(
    (activity) => activity.isLoading
  );
  const noParallelActivities = parallelActivities.every(
    (activity) => !activity.data
  );

  if (isLoadingParallel) {
    return <SkeletonLoader />;
  }

  if (noParallelActivities) {
    return <NoData>No activities</NoData>;
  }

  const getAllStatsFromActivities = (activities: Activity[]) => {
    return getTriathlonStats(activities, activityTotal, false);
  };

  const runningStatsByYear = Object.fromEntries(
    Object.entries(parallelActivitiesObject).map(([key, value]) => [
      key,
      getAllStatsFromActivities(value).runningStats,
    ])
  );

  const ridingStatsByYear = Object.fromEntries(
    Object.entries(parallelActivitiesObject).map(([key, value]) => [
      key,
      getAllStatsFromActivities(value).ridingStats,
    ])
  );

  const swimmingStatsByYear = Object.fromEntries(
    Object.entries(parallelActivitiesObject).map(([key, value]) => [
      key,
      getAllStatsFromActivities(value).swimmingStats,
    ])
  );

  const getData = (year: (typeof years)[number]) => {
    switch (sport) {
      case 'running':
        return cumulativeSum(Object.values(runningStatsByYear[year]));
      case 'cycling':
        return cumulativeSum(Object.values(ridingStatsByYear[year]));
      case 'swimming':
        return cumulativeSum(Object.values(swimmingStatsByYear[year]));
      case 'all':
        return cumulativeSum(
          Object.values(runningStatsByYear[year]).map(
            (value, index) =>
              value +
              Object.values(ridingStatsByYear[year])[index] +
              Object.values(swimmingStatsByYear[year])[index]
          )
        );
    }
  };

  const options = {
    title: {
      text: '',
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: dataTypeLabels[activityTotal],
      },
    },
    xAxis: {
      categories: weekNumbers.map((weekNumber) => formatWeekNumber(weekNumber)),
      title: {
        text: '',
      },
    },
    plotOptions: {
      series: {
        label: {
          connectorAllowed: false,
        },
        marker: {
          radius: 3,
        },
      },
    },
    series: years.map((year) => ({
      type: 'line',
      name: `${sport} ${year}`,
      data: getData(year),
      color: colorsByyear[year as keyof typeof colorsByyear],
    })),
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  };

  return (
    <div id="activities">
      <Head title="Strava Dashboard - activities" />
      <SportSelector
        sport={sport}
        handleRadioChangeSport={handleRadioChangeSport}
      />
      <ActivityTotalOptions
        activityTotal={activityTotal}
        handleRadioChangeActivityTotal={handleRadioChangeActivityTotal}
        dataTypes={dataTypes.filter((e) => e !== 'suffer_score')}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
    </div>
  );
};

export default ActivitiesChart;
